import { VERSION } from './constants.js';
import { createConsentId } from './consent-id.js';
import type { ConsentIdOptions } from './consent-id.js';
import { isCanonicalSdkVersion } from './sdk-version.js';
import type {
  ConsentEventPayload,
  ConsentEventType,
  ConsentState,
} from './types.js';
import {
  hasCanonicalConsentChoices,
  hasOwn,
  isCanonicalConsentId,
  isCanonicalPolicyVersion,
  isCanonicalUtcTimestamp,
  isRecord,
} from './validation.js';

const CLIENT_EVENT_ID_PATTERN = /^gce_[0-9A-HJKMNP-TV-Z]{26}$/;
const SITE_ID_PATTERN = /^site_[a-z0-9][a-z0-9_-]{0,63}$/;
const LOCALE_PATTERN = /^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/;
const EVENT_TYPES: readonly ConsentEventType[] = [
  'accept_all',
  'reject_all',
  'preferences_saved',
];

export interface CreateConsentEventOptions extends ConsentIdOptions {
  readonly siteId: string;
  readonly state: ConsentState;
  readonly eventType: ConsentEventType;
  readonly locale?: string;
}

export function createClientEventId(options: ConsentIdOptions = {}): string {
  return `gce_${createConsentId(options).slice(4)}`;
}

export function isCanonicalClientEventId(value: unknown): value is string {
  return typeof value === 'string' && CLIENT_EVENT_ID_PATTERN.test(value);
}

export function isCanonicalLocale(value: unknown): value is string {
  return typeof value === 'string' && LOCALE_PATTERN.test(value);
}

export function createConsentEvent(
  options: CreateConsentEventOptions,
): ConsentEventPayload {
  const payload: ConsentEventPayload = {
    siteId: options.siteId,
    consentId: options.state.id,
    clientEventId: createClientEventId({
      ...(options.now === undefined ? {} : { now: options.now }),
      ...(options.crypto === undefined ? {} : { crypto: options.crypto }),
    }),
    clientRevision: options.state.revision,
    eventType: options.eventType,
    configurationVersion: options.state.configurationVersion,
    policyVersion: options.state.policyVersion,
    choices: options.state.categories,
    sdkVersion: VERSION,
    ...(options.locale === undefined ? {} : { locale: options.locale }),
    clientOccurredAt: options.state.updatedAt,
  };

  if (!isConsentEventPayload(payload)) {
    throw new TypeError('Consent event payload could not be created safely.');
  }

  return Object.freeze(payload);
}

export function isConsentEventPayload(value: unknown): value is ConsentEventPayload {
  if (!isRecord(value)) return false;

  const requiredKeys = [
    'siteId',
    'consentId',
    'clientEventId',
    'clientRevision',
    'eventType',
    'configurationVersion',
    'policyVersion',
    'choices',
    'sdkVersion',
    'clientOccurredAt',
  ];
  const allowedKeys = [...requiredKeys, 'locale'];
  const keys = Object.keys(value);

  return (
    requiredKeys.every((key) => hasOwn(value, key)) &&
    keys.every((key) => allowedKeys.includes(key)) &&
    SITE_ID_PATTERN.test(String(value.siteId)) &&
    isCanonicalConsentId(value.consentId) &&
    isCanonicalClientEventId(value.clientEventId) &&
    Number.isSafeInteger(value.clientRevision) &&
    (value.clientRevision as number) >= 1 &&
    EVENT_TYPES.includes(value.eventType as ConsentEventType) &&
    Number.isSafeInteger(value.configurationVersion) &&
    (value.configurationVersion as number) >= 1 &&
    isCanonicalPolicyVersion(value.policyVersion) &&
    hasCanonicalConsentChoices(value.choices) &&
    isCanonicalSdkVersion(value.sdkVersion as string) &&
    (!hasOwn(value, 'locale') || isCanonicalLocale(value.locale)) &&
    isCanonicalUtcTimestamp(value.clientOccurredAt)
  );
}
